import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { listenForAuthChanges, logout } from "../../../redux/authSlice";
import type { RootState, AppDispatch } from "../../../redux/store";
import logo from "../../../assets/cosmoola/logo.jpg";
import {
  Home, Settings, Menu, X, ChevronDown, Search, LogOut,
  Bell, Grid3X3, Globe, Key, Webhook, Palette, UserPlus,
  FileText, BarChart3, Users, CreditCard,
} from "lucide-react";
import Rate from "./Rate";


/* ─── THEME ─── */
const D = {
  bg: "#000000", surface: "#0a0a0a", surface2: "#101010",
  border: "#1c1c1c", border2: "#2a2a2a",
  white: "#ffffff", offwhite: "#e8e8e8",
  muted: "#6b6b6b", subtle: "#3a3a3a", gold: "#C9A96E",
};
const L = {
  bg: "#ffffff", surface: "#fafafa", surface2: "#f3f3f3",
  border: "#e5e5e5", border2: "#d4d4d4",
  text: "#0a0a0a", textSoft: "#525252",
  muted: "#a3a3a3", subtle: "#d4d4d4",
};
const GOLD_GRAD = "linear-gradient(135deg, #E8C96A, #C9A84C, #9A7A2E)";

/* ─── NAV ─── */
type NavItem = {
  id: string;
  label: string;
  icon: any;
  children?: { id: string; label: string; icon: any }[];
};

const NAV: NavItem[] = [
  { id: "home",  label: "Home",     icon: Home },
  { id: "rates", label: "Rates",    icon: Globe },
  /* add more top-level items here as you build them */
];

const titles: Record<string, string> = {
  home:  "Management",
  rates: "Exchange Rates",
};

const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
};

/* ══════════════════════════════════════════════════════════════
   MANAGEMENT DASHBOARD
   ══════════════════════════════════════════════════════════════ */
export default function Management() {
  const dispatch  = useDispatch<AppDispatch>();
  const navigate  = useNavigate();
  const params    = useParams<{ userId?: string }>();
  const location  = useLocation();

  /* ── Auth ── */
  const authUser    = useSelector((s: RootState) => s.auth.user);
  const authLoading = useSelector((s: RootState) => s.auth.status === "loading");
  useEffect(() => { dispatch(listenForAuthChanges()); }, [dispatch]);

  /* ── userId — params first, then location state, then Redux ── */
  const userId =
    params.userId ||
    (location.state as any)?.userId ||
    authUser?.userId ||
    null;

  /* ── UI state ── */
  const [view,     setView]     = useState("home");
  const [sidebar,  setSidebar]  = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [notif,    setNotif]    = useState(false);
  const [grid,     setGrid]     = useState(false);
  const [settings, setSettings] = useState(false);
  const [mobile,   setMobile]   = useState(
    typeof window !== "undefined" ? window.innerWidth < 1024 : false
  );
  const popRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const fn = () => setMobile(window.innerWidth < 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  /* close dropdowns on outside click */
  useEffect(() => {
    const fn = (e: MouseEvent) => {
      if (popRef.current && !popRef.current.contains(e.target as Node)) {
        setNotif(false); setGrid(false); setSettings(false);
      }
    };
    document.addEventListener("mousedown", fn);
    return () => document.removeEventListener("mousedown", fn);
  }, []);

  /* ── Nav handlers ── */
  const navClick = useCallback((item: NavItem) => {
    if (item.children) setExpanded(p => p === item.id ? null : item.id);
    else { setView(item.id); if (mobile) setSidebar(false); }
  }, [mobile]);

  const childClick = useCallback((id: string) => {
    setView(id); if (mobile) setSidebar(false);
  }, [mobile]);

  /* ── Page renderer ── */
  const renderPage = () => {
    switch (view) {
      case "rates": return <Rate />;
      default:      return <HomePage userId={userId} authUser={authUser} />;
    }
  };

  /* ── Loading screen ── */
  if (authLoading) return (
    <div style={{ minHeight: "100vh", background: L.bg, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Montserrat',sans-serif" }}>
      <style>{CSS}</style>
      <div style={{ textAlign: "center" }}>
        <div className="mgmt-spin" style={{ width: 36, height: 36, border: `2px solid ${L.border}`, borderTopColor: D.gold, borderRadius: "50%", margin: "0 auto 20px" }} />
        <div style={{ fontSize: 11, color: L.muted, letterSpacing: "0.1em" }}>Loading…</div>
      </div>
    </div>
  );

  /* ══ RENDER ══ */
  return (
    <div style={{ minHeight: "100vh", background: L.bg, fontFamily: "'Montserrat','Cormorant Garamond',system-ui,sans-serif", color: L.text, display: "flex" }}>
      <style>{CSS}</style>

      {/* overlay (mobile) */}
      <AnimatePresence>
        {mobile && sidebar && (
          <motion.div
            key="ov"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSidebar(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 998, backdropFilter: "blur(3px)" }}
          />
        )}
      </AnimatePresence>

      {/* ═══ SIDEBAR ═══ */}
      <aside
        className="mgmt-sidebar"
        style={{
          transform: mobile && !sidebar ? "translateX(-100%)" : "translateX(0)",
          boxShadow: mobile && sidebar ? "6px 0 24px rgba(0,0,0,0.06)" : "none",
        }}
      >
        {/* logo */}
        <div style={{ padding: "20px 20px 16px", borderBottom: `1px solid ${L.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }} onClick={() => setView("home")}>
            <img src={logo} alt="C" style={{ width: 24, height: 24, objectFit: "cover" }} />
            <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em" }}>COSMOOLA</span>
          </div>
          {mobile && (
            <button onClick={() => setSidebar(false)} className="mgmt-icon-btn">
              <X size={18} color={L.textSoft} />
            </button>
          )}
        </div>

        {/* badge */}
        <div style={{ padding: "12px 20px 8px" }}>
          <div style={{ padding: "9px 14px", background: "rgba(201,169,110,0.06)", border: "1px solid rgba(201,169,110,0.2)", display: "flex", alignItems: "center", gap: 7 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", background: D.gold }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#9A7A2E", textTransform: "uppercase" }}>
              Management Console
            </span>
          </div>
        </div>

        {/* search */}
        <div style={{ padding: "4px 20px 14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 12px", background: L.surface, border: `1px solid ${L.border}`, cursor: "text" }}>
            <Search size={14} color={L.muted} />
            <span style={{ fontSize: 11, color: L.muted }}>Search…</span>
            <span style={{ marginLeft: "auto", fontSize: 9, color: L.subtle, background: L.bg, padding: "1px 5px", border: `1px solid ${L.border}` }}>⌘K</span>
          </div>
        </div>

        {/* nav items */}
        <nav style={{ flex: 1, overflowY: "auto", padding: "0 10px" }}>
          {NAV.map(item => {
            const active = view === item.id || item.children?.some(c => c.id === view);
            const exp    = expanded === item.id;
            const Icon   = item.icon;
            return (
              <div key={item.id}>
                <button
                  onClick={() => navClick(item)}
                  className="mgmt-nav-btn"
                  style={{ background: active ? L.surface : "transparent", color: active ? L.text : L.textSoft }}
                >
                  <Icon size={16} strokeWidth={1.5} style={{ opacity: active ? 1 : 0.5 }} />
                  <span style={{ fontSize: 12, fontWeight: active ? 600 : 400, flex: 1, textAlign: "left" }}>{item.label}</span>
                  {item.children && (
                    <ChevronDown size={12} color={L.muted} style={{ transform: exp ? "rotate(180deg)" : "rotate(0)", transition: "transform .2s" }} />
                  )}
                </button>
                <AnimatePresence>
                  {item.children && exp && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }} style={{ overflow: "hidden", paddingLeft: 30 }}
                    >
                      {item.children.map(ch => {
                        const ca   = view === ch.id;
                        const ChIcon = ch.icon;
                        return (
                          <button key={ch.id} onClick={() => childClick(ch.id)} className="mgmt-nav-btn"
                            style={{ background: ca ? `rgba(201,169,110,0.06)` : "transparent", color: ca ? "#9A7A2E" : L.muted, padding: "7px 10px" }}>
                            <ChIcon size={13} strokeWidth={1.5} />
                            <span style={{ fontSize: 11, fontWeight: ca ? 600 : 400 }}>{ch.label}</span>
                          </button>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* footer */}
        <div style={{ padding: "14px 18px", borderTop: `1px solid ${L.border}` }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 28, height: 28, background: GOLD_GRAD, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: D.bg }}>
              {authUser?.email?.charAt(0)?.toUpperCase() || "A"}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {authUser?.email || "Admin"}
              </div>
              <div style={{ fontSize: 9, color: L.muted }}>Cosmoola Staff</div>
            </div>
          </div>
        </div>
      </aside>

      {/* ═══ MAIN ═══ */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>

        {/* ── HEADER ── */}
        <header
          ref={popRef}
          style={{ height: 58, background: "rgba(0,0,0,0.95)", backdropFilter: "blur(12px)", borderBottom: `1px solid ${D.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 20px", position: "sticky", top: 0, zIndex: 100 }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            {mobile && (
              <button onClick={() => setSidebar(true)} className="mgmt-icon-btn">
                <Menu size={18} color={D.offwhite} />
              </button>
            )}
            <h1 style={{ fontSize: 13, fontWeight: 600, color: D.white, letterSpacing: "0.04em" }}>
              {titles[view] || "Management"}
            </h1>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 2 }}>

            {/* grid quick-access */}
            <div style={{ position: "relative" }}>
              <button onClick={() => { setGrid(!grid); setNotif(false); setSettings(false); }} className="mgmt-icon-btn" style={{ background: grid ? "rgba(255,255,255,0.06)" : "transparent" }}>
                <Grid3X3 size={16} color={grid ? D.white : D.muted} />
              </button>
              <AnimatePresence>
                {grid && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} className="mgmt-dropdown" style={{ width: 200 }}>
                    <div style={{ padding: "10px 14px 6px" }}>
                      <span style={{ fontSize: 9, letterSpacing: "0.12em", color: D.muted, textTransform: "uppercase" }}>Quick Access</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, padding: "0 6px 10px" }}>
                      {[
                        { l: "Home",  i: Home,  id: "home" },
                        { l: "Rates", i: Globe, id: "rates" },
                      ].map(q => {
                        const QI = q.i;
                        return (
                          <button key={q.id} onClick={() => { setView(q.id); setGrid(false); }} className="mgmt-grid-btn">
                            <QI size={16} color={D.muted} strokeWidth={1.5} />
                            <span style={{ fontSize: 9, color: D.muted }}>{q.l}</span>
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* bell */}
            <div style={{ position: "relative" }}>
              <button onClick={() => { setNotif(!notif); setGrid(false); setSettings(false); }} className="mgmt-icon-btn" style={{ position: "relative", background: notif ? "rgba(255,255,255,0.06)" : "transparent" }}>
                <Bell size={16} color={notif ? D.white : D.muted} />
                <div style={{ position: "absolute", top: 7, right: 7, width: 5, height: 5, borderRadius: "50%", background: "#C44D4D", border: `1.5px solid ${D.bg}` }} />
              </button>
              <AnimatePresence>
                {notif && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} className="mgmt-dropdown" style={{ width: 280 }}>
                    <div style={{ padding: "14px 18px", borderBottom: `1px solid ${D.border}`, display: "flex", justifyContent: "space-between" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: D.white }}>Notifications</span>
                      <span style={{ fontSize: 9, color: D.gold, cursor: "pointer" }}>Mark all read</span>
                    </div>
                    <div style={{ padding: "20px 18px", textAlign: "center" }}>
                      <div style={{ fontSize: 11, color: D.muted }}>No new notifications.</div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* settings */}
            <div style={{ position: "relative" }}>
              <button onClick={() => { setSettings(!settings); setGrid(false); setNotif(false); }} className="mgmt-icon-btn" style={{ background: settings ? "rgba(255,255,255,0.06)" : "transparent" }}>
                <Settings size={16} color={settings ? D.white : D.muted} />
              </button>
              <AnimatePresence>
                {settings && (
                  <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 6 }} className="mgmt-dropdown" style={{ width: 200 }}>
                    {[
                      { l: "Account",  p: "/managementsettings", i: Settings },
                      { l: "Team",     p: "/team",               i: UserPlus },
                      { l: "API Keys", p: "/apikeys",            i: Key },
                      { l: "Webhooks", p: "/webhooks",           i: Webhook },
                      { l: "Branding", p: "/settings/branding",  i: Palette },
                    ].map(s => {
                      const SI = s.i;
                      return (
                        <button key={s.l} onClick={() => { navigate(s.p); setSettings(false); }} className="mgmt-settings-btn">
                          <SI size={14} color={D.muted} strokeWidth={1.5} />{s.l}
                        </button>
                      );
                    })}
                    <div style={{ borderTop: `1px solid ${D.border}`, margin: "3px 0" }} />
                    <button onClick={() => dispatch(logout())} className="mgmt-settings-btn" style={{ color: "#C44D4D" }}>
                      <LogOut size={14} color="#C44D4D" strokeWidth={1.5} />Sign out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* avatar */}
            <div style={{ width: 28, height: 28, background: GOLD_GRAD, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: D.bg, marginLeft: 6, cursor: "pointer" }}>
              {authUser?.email?.charAt(0)?.toUpperCase() || "A"}
            </div>
          </div>
        </header>

        {/* ── CONTENT ── */}
        <main style={{ flex: 1, padding: mobile ? "20px 16px" : "28px 28px", overflowY: "auto" }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={view}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.2 }}
            >
              {renderPage()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  );
}

/* ═══ HOME PAGE ═══ */
function HomePage({ userId, authUser }: { userId: string | null; authUser: any }) {
  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 26, fontFamily: "'Cormorant Garamond',serif", fontWeight: 400, color: L.text, marginBottom: 6 }}>
          {greeting()}, <span style={{ fontStyle: "italic" }}>{authUser?.email?.split("@")[0] || "Admin"}</span>
        </h2>
        <p style={{ fontSize: 11, color: L.textSoft, letterSpacing: "0.04em" }}>
          Cosmoola Management Console — internal tools &amp; configuration.
        </p>
        {userId && (
          <div style={{ marginTop: 10, fontSize: 10, color: L.muted, fontFamily: "'Courier New',monospace" }}>
            uid: {userId}
          </div>
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
        {[
          { label: "Exchange Rates",   desc: "Manage global currency rates served to merchants.",    icon: Globe,    id: "rates" },
          /* add more tiles here as sections are built */
        ].map((tile, i) => {
          const TI = tile.icon;
          return (
            <motion.div
              key={tile.id}
              initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }}
              style={{ background: L.bg, border: `1px solid ${L.border}`, padding: 24, cursor: "pointer", transition: "all .2s" }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(201,169,110,0.4)"; e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.04)"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = L.border; e.currentTarget.style.boxShadow = "none"; }}
            >
              <div style={{ width: 36, height: 36, background: "rgba(201,169,110,0.08)", border: "1px solid rgba(201,169,110,0.2)", display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
                <TI size={18} color="#9A7A2E" strokeWidth={1.5} />
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: L.text, marginBottom: 4 }}>{tile.label}</div>
              <div style={{ fontSize: 11, color: L.textSoft, lineHeight: 1.6 }}>{tile.desc}</div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── CSS ─── */
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400&family=Montserrat:wght@300;400;500;600;700;800&display=swap');
*,::before,::after{box-sizing:border-box;margin:0;padding:0}
body{background:#fff}button{font-family:inherit}
::selection{background:rgba(201,169,110,0.3);color:#0a0a0a}
::-webkit-scrollbar{width:4px}::-webkit-scrollbar-track{background:transparent}::-webkit-scrollbar-thumb{background:#d4d4d4;border-radius:2px}
@keyframes spin{to{transform:rotate(360deg)}}
.mgmt-spin{animation:spin .8s linear infinite}

.mgmt-sidebar{width:248px;background:#fff;border-right:1px solid #e5e5e5;display:flex;flex-direction:column;position:sticky;top:0;height:100vh;z-index:999;transition:transform .3s cubic-bezier(.4,0,.2,1)}
@media(max-width:1023px){.mgmt-sidebar{position:fixed;left:0}}

.mgmt-icon-btn{width:34px;height:34px;border:none;background:transparent;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background .15s;border-radius:0}
.mgmt-icon-btn:hover{background:rgba(255,255,255,0.06)}

.mgmt-nav-btn{width:100%;padding:9px 10px;border:none;display:flex;align-items:center;gap:9px;cursor:pointer;transition:all .15s;margin-bottom:1px;font-family:inherit;background:transparent}
.mgmt-nav-btn:hover{background:#fafafa}

.mgmt-dropdown{position:absolute;top:calc(100% + 6px);right:0;background:#0a0a0a;border:1px solid #1c1c1c;box-shadow:0 10px 32px rgba(0,0,0,0.2);overflow:hidden;z-index:200}

.mgmt-grid-btn{padding:10px 6px;border:none;background:transparent;cursor:pointer;text-align:center;display:flex;flex-direction:column;align-items:center;gap:4px;transition:background .1s}
.mgmt-grid-btn:hover{background:rgba(255,255,255,0.04)}

.mgmt-settings-btn{width:100%;padding:9px 14px;border:none;background:transparent;text-align:left;cursor:pointer;font-size:11px;color:#6b6b6b;font-weight:500;display:flex;align-items:center;gap:8px;transition:background .1s;font-family:inherit}
.mgmt-settings-btn:hover{background:rgba(255,255,255,0.04)}
`;